import { useCallback, useState } from 'react';
import type { RenderMode } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;
const key = (contentKey: string) => `cs:dismissed:${contentKey}`;

// Storage can be missing or throw (private windows, blocked third-party
// storage in the iframe). Dismissal then lasts until the page reloads.
function readUntil(contentKey: string): number {
    try {
        return Number(window.localStorage.getItem(key(contentKey))) || 0;
    } catch {
        return 0;
    }
}

function writeUntil(contentKey: string, until: number) {
    try {
        window.localStorage.setItem(key(contentKey), String(until));
    } catch {
        // See above.
    }
}

/**
 * Whether the visitor closed this version of the content. On the live site
 * it is remembered for `rememberDays`; in preview it lasts for the page; in
 * the editor the button does nothing, so the owner never loses sight of it.
 */
export function useDismissed(contentKey: string | null, rememberDays: number, mode: RenderMode) {
    const [dismissed, setDismissed] = useState(
        () => mode === 'site' && contentKey !== null && readUntil(contentKey) > Date.now(),
    );

    const dismiss = useCallback(() => {
        if (mode === 'editor') return;
        if (mode === 'site' && contentKey)
            writeUntil(contentKey, Date.now() + rememberDays * DAY_MS);
        setDismissed(true);
    }, [contentKey, rememberDays, mode]);

    return [dismissed, dismiss] as const;
}
